import { errorStatus, type SessionClient } from '../http';
import type { Access, Identity, OrgNodeTree } from './types';

/** `{ data: X }` → X; anything else as-is. The auth routes answer through ResponseHandler. */
function unwrapData<T>(body: unknown): T {
  if (body && typeof body === 'object' && 'data' in body) {
    return (body as { data: T }).data;
  }
  return body as T;
}

/**
 * The logged-in user, or null when there is no session.
 *
 * A 401 from `/auth/whoami` may only mean the access token expired: the refresh cookie is tried
 * once, then whoami again (the Angular rehydration). `/auth/` paths are not refreshed by the
 * session client itself, so this is the one place it happens for whoami.
 */
export async function fetchIdentity(client: SessionClient): Promise<Identity | null> {
  const whoami = async () => {
    const body = unwrapData<{ identity?: Identity } | null>(await client.json('/auth/whoami'));
    if (!body?.identity?.roleContext) {
      throw new Error('whoami answered without an identity');
    }
    return body.identity;
  };

  try {
    return await whoami();
  } catch (error) {
    if (errorStatus(error) !== 401) {
      throw error;
    }
  }
  if (!(await client.refresh())) {
    return null;
  }
  try {
    return await whoami();
  } catch (error) {
    if (errorStatus(error) === 401) {
      return null;
    }
    throw error;
  }
}

/**
 * `POST /auth/login`. The backend sets the session cookies; the caller then reads the identity.
 * Fails with the ApiError as sent: 401 for wrong credentials, 403 with `body.reason` for a
 * locked account.
 */
export async function postLogin(
  client: SessionClient,
  credentials: { email: string; password: string },
): Promise<void> {
  await client.json('/auth/login', { method: 'POST', json: credentials });
}

/** `POST /auth/logout` — the only way to clear the httpOnly session cookies. */
export async function postLogout(client: SessionClient): Promise<void> {
  await client.json('/auth/logout', { method: 'POST' });
}

/** `GET /org-nodes/tree`, in either of the shapes it arrives in; empty when there is none. */
export async function fetchOrgNodeTree(client: SessionClient): Promise<OrgNodeTree> {
  const body = await client.json<unknown>('/org-nodes/tree');
  const tree = (
    body && typeof body === 'object' && 'nodes' in body ? body : unwrapData<unknown>(body)
  ) as Partial<OrgNodeTree> | null;
  return {
    root: tree?.root ?? null,
    nodes: Array.isArray(tree?.nodes) ? tree.nodes : [],
    pathVersion: tree?.pathVersion ?? 1,
  };
}

/** `GET /me/access` at `orgNodeId` (the user's home node when null). Answers unwrapped. */
export async function fetchAccess(
  client: SessionClient,
  orgNodeId: string | null,
): Promise<Access> {
  return client.json<Access>('/me/access', { query: { orgNodeId } });
}

/** `POST /auth/forgot-password`. Always answers alike, so an address cannot be probed. */
export async function requestPasswordReset(client: SessionClient, email: string): Promise<void> {
  await client.json('/auth/forgot-password', { method: 'POST', json: { email } });
}

/** Why a reset link cannot be used, as `GET /auth/reset-password/validate/:token` says. */
export type ResetTokenProblem = 'TOKEN_EXPIRED' | 'TOKEN_ALREADY_USED' | 'INVALID_TOKEN';

export interface ResetTokenValidation {
  valid: boolean;
  user?: { id: string; name: string; email: string; language?: string } | null;
  error?: ResetTokenProblem | string;
}

/** `GET /auth/reset-password/validate/:token` (`{ data: … }`). */
export async function validateResetToken(
  client: SessionClient,
  token: string,
): Promise<ResetTokenValidation> {
  const body = await client.json<unknown>(
    `/auth/reset-password/validate/${encodeURIComponent(token)}`,
  );
  const validation = unwrapData<ResetTokenValidation | null>(body);
  return validation ?? { valid: false, error: 'INVALID_TOKEN' };
}

/** `POST /auth/reset-password` — set a new password with a reset token. 400 when refused. */
export async function resetPassword(
  client: SessionClient,
  token: string,
  password: string,
): Promise<void> {
  await client.json('/auth/reset-password', { method: 'POST', json: { token, password } });
}

/** `POST /auth/change-password` — the logged-in user's own password. */
export async function changePassword(
  client: SessionClient,
  oldPassword: string,
  newPassword: string,
): Promise<void> {
  await client.json('/auth/change-password', {
    method: 'POST',
    json: { oldPassword, newPassword },
  });
}
